// composables/useZodI18n.ts
import { z } from 'zod'

export const useZodI18n = () => {
  const { t } = useI18n()

  const errorMap: z.ZodErrorMap = (issue, ctx) => {
    // Custom message handling
    if (issue.message) {
      // If message starts with validation., treat as i18n key
      if (issue.message.startsWith('validation.')) {
        return { message: t(issue.message) }
      }
      // Otherwise use as-is
      return { message: issue.message }
    }

    // Automatic error mapping based on issue type
    switch (issue.code) {
      case z.ZodIssueCode.invalid_type:
        if (issue.received === 'undefined' || issue.received === 'null') {
          return { message: t('validation.field_is_required') }
        }
        return {
          message: t('validation.invalid_type', {
            expected: issue.expected,
            received: issue.received,
          }),
        }

      case z.ZodIssueCode.too_small:
        if (issue.type === 'string') {
          if (issue.exact) {
            return {
              message: t('validation.field_exact_length', {
                count: issue.minimum,
              }),
            }
          }
          return {
            message: t('validation.field_min_length', { count: issue.minimum }),
          }
        }
        if (issue.type === 'number') {
          return {
            message: t('validation.number_min_value', { min: issue.minimum }),
          }
        }
        if (issue.type === 'array') {
          return {
            message: t('validation.array_min_items', { count: issue.minimum }),
          }
        }
        break

      case z.ZodIssueCode.too_big:
        if (issue.type === 'string') {
          if (issue.exact) {
            return {
              message: t('validation.field_exact_length', {
                count: issue.maximum,
              }),
            }
          }
          return {
            message: t('validation.field_max_length', { count: issue.maximum }),
          }
        }
        if (issue.type === 'number') {
          return {
            message: t('validation.number_max_value', { max: issue.maximum }),
          }
        }
        if (issue.type === 'array') {
          return {
            message: t('validation.array_max_items', { count: issue.maximum }),
          }
        }
        break

      case z.ZodIssueCode.invalid_string:
        switch (issue.validation) {
          case 'email':
            return { message: t('validation.invalid_email') }
          case 'url':
            return { message: t('validation.invalid_url') }
          case 'uuid':
            return { message: t('validation.invalid_uuid') }
          case 'cuid':
            return { message: t('validation.invalid_cuid') }
          case 'regex':
            return { message: t('validation.invalid_format') }
          case 'datetime':
            return { message: t('validation.invalid_datetime') }
          default:
            return { message: t('validation.invalid_string_format') }
        }

      case z.ZodIssueCode.invalid_date:
        return { message: t('validation.invalid_date') }

      case z.ZodIssueCode.invalid_enum_value:
        return {
          message: t('validation.invalid_enum_value', {
            options: issue.options?.join(', ') || '',
          }),
        }

      case z.ZodIssueCode.unrecognized_keys:
        return {
          message: t('validation.unrecognized_keys', {
            keys: issue.keys.join(', '),
          }),
        }

      case z.ZodIssueCode.invalid_arguments:
        return { message: t('validation.invalid_arguments') }

      case z.ZodIssueCode.invalid_return_type:
        return { message: t('validation.invalid_return_type') }

      case z.ZodIssueCode.invalid_union:
        return { message: t('validation.invalid_union') }

      case z.ZodIssueCode.invalid_union_discriminator:
        return { message: t('validation.invalid_union_discriminator') }

      case z.ZodIssueCode.invalid_literal:
        return {
          message: t('validation.invalid_literal', {
            expected: JSON.stringify(issue.expected),
          }),
        }

      case z.ZodIssueCode.custom:
        // Handle custom validation messages
        if (issue.params?.i18nKey) {
          return {
            message: t(issue.params.i18nKey, issue.params.values || {}),
          }
        }
        return { message: t('validation.custom_validation_failed') }

      case z.ZodIssueCode.not_multiple_of:
        return {
          message: t('validation.not_multiple_of', {
            multipleOf: issue.multipleOf,
          }),
        }

      case z.ZodIssueCode.not_finite:
        return { message: t('validation.must_be_finite') }
    }

    // Fallback to default error
    return { message: ctx.defaultError }
  }

  // Set the error map globally
  z.setErrorMap(errorMap)

  return {
    errorMap,
    setupZodI18n: () => z.setErrorMap(errorMap),
  }
}

// Helper functions for common validations
export const zodValidationHelpers = () => {
  const { t } = useI18n()

  return {
    // Custom validation for Serbian VAT number (PIB)
    vatNumber: () =>
      z
        .string()
        .length(9, { message: 'validation.vat_number_length' })
        .regex(/^\d{9}$/, { message: 'validation.vat_number_format' }),

    // Custom validation for Serbian postal code
    postalCode: () =>
      z
        .string()
        .length(5, { message: 'validation.postal_code_length' })
        .regex(/^\d{5}$/, { message: 'validation.postal_code_format' }),

    // Phone number validation
    phoneNumber: () =>
      z
        .string()
        .min(8, { message: 'validation.phone_min_length' })
        .regex(/^[\d\s\-\+\(\)]+$/, { message: 'validation.phone_format' }),

    // Registration number validation (Serbian company registration)
    registrationNumber: () =>
      z
        .string()
        .length(8, { message: 'validation.registration_number_length' })
        .regex(/^\d{8}$/, { message: 'validation.registration_number_format' }),

    // Custom validation with i18n key
    customValidation: (
      validationFn: (value: any) => boolean,
      i18nKey: string,
      values?: Record<string, any>
    ) => {
      return z.custom(validationFn, {
        params: { i18nKey, values },
      })
    },
  }
}
